import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Button,
  Tooltip,
  Badge,
  Divider,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Search,
  Visibility,
  Email,
  Phone,
  Delete,
  Send,
  PersonAdd,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';

const STATUS_CONFIG = {
  new: { label: 'New', color: '#2196f3', bgColor: '#e3f2fd' },
  contacted: { label: 'Contacted', color: '#ff9800', bgColor: '#fff3e0' },
  in_progress: { label: 'In Progress', color: '#9c27b0', bgColor: '#f3e5f5' },
  completed: { label: 'Completed', color: '#4caf50', bgColor: '#e8f5e9' },
  spam: { label: 'Spam', color: '#f44336', bgColor: '#ffebee' },
  archived: { label: 'Archived', color: '#9e9e9e', bgColor: '#f5f5f5' },
};

const URGENCY_CONFIG = {
  urgent: { label: 'Urgent', color: 'error', icon: '🔥' },
  high: { label: 'High', color: 'warning', icon: '⚡' },
  medium: { label: 'Medium', color: 'info', icon: '📌' },
  low: { label: 'Low', color: 'default', icon: '📋' },
};

const ContactRequests = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contact/requests');
      setRequests(response.data?.requests || []);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      showSnackbar('Failed to fetch contact requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/contact/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await api.put(`/contact/requests/${requestId}/status`, { status: newStatus });
      await fetchRequests();
      await fetchStats();
      showSnackbar('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleMarkAsSpam = async (requestId) => {
    await handleStatusChange(requestId, 'spam');
  };

  const handleArchive = async (requestId) => {
    try {
      await api.delete(`/contact/requests/${requestId}`);
      await fetchRequests();
      await fetchStats();
      showSnackbar('Request archived successfully');
    } catch (error) {
      console.error('Error archiving request:', error);
      showSnackbar('Failed to archive request', 'error');
    }
  };

  const getFilteredRequests = () => {
    return requests.filter((request) => {
      const matchesSearch =
        !searchQuery ||
        request.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.phone?.includes(searchQuery) ||
        request.message?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      const matchesUrgency = urgencyFilter === 'all' || request.urgency_level === urgencyFilter;

      return matchesSearch && matchesStatus && matchesUrgency;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Contact Requests
        </Typography>
      </Box>

      {stats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  New Requests
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  <Badge badgeContent={stats.status_breakdown?.new || 0} color="error" max={999}>
                    {stats.status_breakdown?.new || 0}
                  </Badge>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Recent (7 Days)
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.recent_requests_7d || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Avg Lead Score
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.average_lead_score || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Conversion Rate
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.conversion_rate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, email, or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="contacted">Contacted</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="spam">Spam</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={urgencyFilter}
                  label="Urgency"
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                >
                  <MenuItem value="all">All Urgency</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Info</TableCell>
                  <TableCell>Message Preview</TableCell>
                  <TableCell>Lead Score</TableCell>
                  <TableCell>Urgency</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredRequests().length > 0 ? (
                  getFilteredRequests().map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {request.name?.[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium">
                            {request.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                          <Email fontSize="small" color="action" />
                          {request.email}
                        </Typography>
                        {request.phone && (
                          <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                            <Phone fontSize="small" color="action" />
                            {request.phone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                          {request.message?.substring(0, 80)}
                          {request.message?.length > 80 ? '...' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.lead_score || 0}
                          size="small"
                          color={
                            request.lead_score >= 70
                              ? 'success'
                              : request.lead_score >= 50
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {request.urgency_level ? (
                          <Chip
                            label={URGENCY_CONFIG[request.urgency_level]?.label || request.urgency_level}
                            color={URGENCY_CONFIG[request.urgency_level]?.color || 'default'}
                            size="small"
                            icon={
                              <span style={{ fontSize: '0.875rem' }}>
                                {URGENCY_CONFIG[request.urgency_level]?.icon}
                              </span>
                            }
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_CONFIG[request.status]?.label || request.status}
                          size="small"
                          sx={{
                            bgcolor: STATUS_CONFIG[request.status]?.bgColor,
                            color: STATUS_CONFIG[request.status]?.color,
                            fontWeight: 'medium',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {request.created_at
                          ? format(new Date(request.created_at), 'MMM dd, yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(request)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark as Spam">
                            <IconButton
                              size="small"
                              onClick={() => handleMarkAsSpam(request.id)}
                              disabled={request.status === 'spam'}
                            >
                              <Block fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleArchive(request.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No contact requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ContactDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        request={selectedRequest}
        onStatusChange={handleStatusChange}
        onOpenResponse={() => {
          setDetailDialogOpen(false);
          setResponseDialogOpen(true);
        }}
        onRefresh={fetchRequests}
      />

      <ResponseDialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        request={selectedRequest}
        onSuccess={() => {
          fetchRequests();
          fetchStats();
          showSnackbar('Response sent successfully');
          setResponseDialogOpen(false);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ContactDetailDialog = ({ open, onClose, request, onStatusChange, onOpenResponse, onRefresh }) => {
  if (!request) return null;

  const handleConvertToLead = async () => {
    try {
      // This would navigate to the leads page with pre-filled data
      // For now, we'll just show a success message
      alert('Convert to Lead feature - to be implemented');
    } catch (error) {
      console.error('Error converting to lead:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Contact Request Details</Typography>
          <Chip
            label={STATUS_CONFIG[request.status]?.label || request.status}
            sx={{
              bgcolor: STATUS_CONFIG[request.status]?.bgColor,
              color: STATUS_CONFIG[request.status]?.color,
              fontWeight: 'medium',
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Contact Information
            </Typography>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" fontWeight="bold">
                  {request.name}
                </Typography>
                {request.urgency_level && (
                  <Chip
                    label={URGENCY_CONFIG[request.urgency_level]?.label}
                    color={URGENCY_CONFIG[request.urgency_level]?.color}
                    size="small"
                  />
                )}
              </Box>
              <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" />
                {request.email}
              </Typography>
              {request.phone && (
                <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                  <Phone fontSize="small" />
                  {request.phone}
                </Typography>
              )}
            </Stack>
          </Grid>

          {request.property_address && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Property Address
              </Typography>
              <Typography variant="body2">{request.property_address}</Typography>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Estimated Items
            </Typography>
            <Typography variant="body2">{request.estimated_items || 'Not specified'}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Lead Score
            </Typography>
            <Chip
              label={request.lead_score || 0}
              color={
                request.lead_score >= 70 ? 'success' : request.lead_score >= 50 ? 'warning' : 'default'
              }
            />
          </Grid>

          {request.preferred_contact_method && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Preferred Contact Method
              </Typography>
              <Typography variant="body2">{request.preferred_contact_method}</Typography>
            </Grid>
          )}

          {request.best_time_to_call && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Best Time to Call
              </Typography>
              <Typography variant="body2">{request.best_time_to_call}</Typography>
            </Grid>
          )}

          {request.referral_source && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Referral Source
              </Typography>
              <Typography variant="body2">{request.referral_source}</Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Message
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {request.message || 'No message provided'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Change Status
            </Typography>
            <FormControl fullWidth>
              <Select
                value={request.status}
                onChange={(e) => {
                  onStatusChange(request.id, e.target.value);
                  onClose();
                }}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="spam">Spam</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {request.created_at && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Submitted on {format(new Date(request.created_at), 'MMMM dd, yyyy \'at\' h:mm a')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<PersonAdd />}
          onClick={handleConvertToLead}
          color="primary"
        >
          Convert to Lead
        </Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={onOpenResponse}
          color="primary"
        >
          Send Response
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ResponseDialog = ({ open, onClose, request, onSuccess }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    send_copy_to_admin: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (request && open) {
      setFormData({
        subject: `Re: Your inquiry - ${request.name}`,
        message: `Dear ${request.name},\n\nThank you for contacting PaceEstate.\n\n`,
        send_copy_to_admin: true,
      });
    }
  }, [request, open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!request) return;

    setError('');
    setSubmitting(true);

    try {
      await api.post(`/contact/requests/${request.id}/respond`, formData);
      onSuccess();
      setFormData({ subject: '', message: '', send_copy_to_admin: true });
    } catch (err) {
      console.error('Error sending response:', err);
      setError(err.response?.data?.error || 'Failed to send response');
    } finally {
      setSubmitting(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Send Response to {request.name}</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              multiline
              rows={8}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting} startIcon={<Send />}>
            {submitting ? 'Sending...' : 'Send Response'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactRequests;
